import React, { useState, useRef, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { api, API_BASE } from '../utils/api'; 
import { Send, AlertCircle, Loader2, CheckCircle, HelpCircle, List, Paperclip, Globe } from 'lucide-react';

// --- [START] Translations Object ---
const translations = {
  th: {
    langToggle: "EN",
    title: "ยื่นใบลืมลงเวลา",
    subtitle: "กรอกแบบฟอร์มเพื่อส่งคำร้อง (กรณีลืมสแกนเข้า หรือ สแกนออก)",
    formTitle: "กรอกรายละเอียดการลืมลงเวลา",
    dateLabel: "วันที่ลืมลงเวลา",
    scanTypeLabel: "ประเภทการลืม",
    scanTypePlaceholder: "-- กรุณาเลือก --",
    scanTypeIn: "ลืมลงเวลาเข้า",
    scanTypeOut: "ลืมลงเวลาออก",
    reasonLabel: "เหตุผลที่ลืม",
    reasonPlaceholder: "กรุณาระบุเหตุผลที่ลืมลงเวลา...",
    attachmentLabel: "แนบหลักฐาน (ภาพจากกล้องวงจรปิด / รูปภาพอ้างอิง)",
    attachmentButton: "เลือกไฟล์รูปภาพ",
    attachmentRemoving: "ลบรูปภาพ",
    signatureLabel: "ลงลายมือชื่อ (กรุณาลงลายมือชื่อบริเวณตรงกลาง)",
    signatureClear: "ล้างลายเซ็น",
    submitButtonText: "ยื่นใบคำร้อง (รอพิจารณา)",
    submitButtonSigning: "กรุณาลงลายมือชื่อก่อนส่ง",
    submitButtonSubmitting: "กำลังส่งข้อมูล...",
    listTitle: "ประวัติคำร้องของฉัน",
    listLoading: "กำลังโหลดประวัติ...",
    listError: "ไม่สามารถดึงประวัติคำร้องได้:",
    listEmpty: "ไม่พบประวัติคำร้อง",
    listHeaderDate: "วันที่",
    listHeaderType: "ประเภท",
    listHeaderReason: "เหตุผล",
    listHeaderStatus: "สถานะ",
    listHeaderAction: "จัดการ",
    listStatusPending: "รอพิจารณา",
    listStatusApproved: "อนุมัติแล้ว",
    listStatusRejected: "ไม่อนุมัติ",
    errorSignature: "กรุณาลงลายมือชื่อผู้ยื่น",
    errorFormIncomplete: "กรุณากรอกข้อมูลให้ครบถ้วน: วันที่, ประเภทการลืม และ เหตุผล",
    errorSubmit: "ไม่สามารถยื่นใบคำร้องได้",
    errorConnection: "เกิดข้อผิดพลาดในการเชื่อมต่อ:",
    successSubmit: "ยื่นใบคำร้องลืมลงเวลาสำเร็จ เรื่องของท่านจะถูกส่งให้ผู้ดูแลระบบพิจารณา",
  },
  en: {
    langToggle: "TH",
    title: "Forgot Scan Request",
    subtitle: "Fill out the form to submit a request (missing check-in or check-out)",
    formTitle: "Request Details",
    dateLabel: "Date of Missing Scan",
    scanTypeLabel: "Missing Scan Type",
    scanTypePlaceholder: "-- Please select --",
    scanTypeIn: "Forgot Check-In",
    scanTypeOut: "Forgot Check-Out",
    reasonLabel: "Reason",
    reasonPlaceholder: "Please provide a reason...",
    attachmentLabel: "Attach Evidence (CCTV or reference image)",
    attachmentButton: "Choose Image File",
    attachmentRemoving: "Remove Image",
    signatureLabel: "Signature (Please sign in the center)",
    signatureClear: "Clear Signature",
    submitButtonText: "Submit Request (Pending)",
    submitButtonSigning: "Please Sign Before Submitting",
    submitButtonSubmitting: "Submitting...",
    listTitle: "My Request History",
    listLoading: "Loading history...",
    listError: "Could not fetch request history:",
    listEmpty: "No request history found.",
    listHeaderDate: "Date",
    listHeaderType: "Type",
    listHeaderReason: "Reason",
    listHeaderStatus: "Status",
    listHeaderAction: "Actions",
    listStatusPending: "Pending",
    listStatusApproved: "Approved",
    listStatusRejected: "Rejected",
    errorSignature: "Please provide your signature.",
    errorFormIncomplete: "Please complete all required fields.",
    errorSubmit: "Failed to submit request.",
    errorConnection: "Connection error:",
    successSubmit: "Request submitted successfully! It will be sent for review.",
  }
};

const formatDateThai = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Bangkok' };
  return date.toLocaleDateString('th-TH', options);
};

const formatDateEng = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// --- [START] Helper Function for Printing Attachment ---
const handlePrintAttachment = (attachmentDataUrl) => {
  let printWindow;
  try {
    let imageUrl;
    if (attachmentDataUrl.startsWith('data:image')) {
      imageUrl = attachmentDataUrl;
    } else {
      const cleanPath = attachmentDataUrl.startsWith('/') ? attachmentDataUrl.substring(1) : attachmentDataUrl;
      imageUrl = `${API_BASE}${cleanPath}`;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Evidence Attachment</title>
        <style>
          @page { size: A4 portrait; margin: 1.5cm; }
          body { margin: 0; padding: 0; text-align: center; }
          img { max-width: 100%; max-height: 95vh; object-fit: contain; }
        </style>
      </head>
      <body>
        <div><img src="${imageUrl}" alt="Evidence" /></div>
      </body>
      </html>
    `;
    
    printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Cannot open print window. Please check your pop-up blocker settings.');
      return;
    }
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus(); 
      printWindow.print();
    }, 1000); 

  } catch (err) {
    console.error("Error printing attachment:", err);
    if (printWindow) {
      printWindow.document.write(`<h3>Error: ${err.message}</h3>`);
    } else {
      alert("Error displaying attachment: " + err.message);
    }
  }
};
// --- [END] Helper Function for Printing Attachment ---

const ForgotScanRequestForm = ({ currentUser }) => {
  const user = currentUser;
  
  const [lang, setLang] = useState('th');
  const t = translations[lang];

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [scanType, setScanType] = useState(''); 
  const [reason, setReason] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const sigPad = useRef(null);
  const [signatureEmpty, setSignatureEmpty] = useState(true);

  // States สำหรับประวัติการยื่น
  const [requests, setRequests] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const fetchRequests = useCallback(async () => {
    if (!user || !user.employee_id) return;
    setLoadingList(true);
    setListError('');
    try {
      const data = await api.getForgotScanRequests(user.employee_id);
      if (Array.isArray(data)) {
        setRequests(data);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setListError(t.listError + err.message);
    } finally {
      setLoadingList(false);
    }
  }, [user, t.listError]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setAttachment(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sigPad.current.isEmpty()) {
      setSignatureEmpty(true);
      setMessage({ type: 'error', text: t.errorSignature });
      return;
    }
    setSignatureEmpty(false);

    if (!date || !scanType || !reason.trim()) {
      setMessage({ type: 'error', text: t.errorFormIncomplete });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      // ✅ ใช้ toDataURL แบบเดียวกับ LateRequestForm เพื่อป้องกัน Error
      const signatureDataUrl = sigPad.current.toDataURL('image/png');

      const payload = {
        employee_id: user.employee_id,
        request_date: date,
        scan_type: scanType,
        reason: reason,
        applicant_signature: signatureDataUrl,
        attachment_image: attachment
      };

      const res = await api.submitForgotScanRequest(payload);
      
      if (res.success) {
        setMessage({ type: 'success', text: t.successSubmit });
        // Reset form
        setDate(new Date().toISOString().split('T')[0]);
        setScanType('');
        setReason('');
        sigPad.current.clear();
        setSignatureEmpty(true);
        setAttachment(null);
        fetchRequests(); 
      } else {
        setMessage({ type: 'error', text: res.error || t.errorSubmit });
      }
    } catch (err) {
      console.error("Error submitting request:", err);
      setMessage({ type: 'error', text: t.errorConnection + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLanguage = () => {
    setLang(lang === 'th' ? 'en' : 'th');
    setMessage({ type: '', text: '' }); 
  };

  const formatDateByLang = (dateString) => {
    return lang === 'th' ? formatDateThai(dateString) : formatDateEng(dateString);
  };

  // ✅ ถ้ายังไม่มีข้อมูล user ให้แสดงตัวโหลด (ป้องกัน Cannot read properties of undefined)
  if (!user || !user.employee_id) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
        <span className="text-gray-500 font-medium">กำลังเตรียมข้อมูลผู้ใช้...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      
      {/* (1) Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-600 mt-2">{t.subtitle}</p>
        </div>
        <button
          onClick={toggleLanguage}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <Globe className="h-5 w-5 mr-2 text-gray-500" />
          {t.langToggle}
        </button>
      </div>

      {/* (2) ฟอร์ม */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <HelpCircle className="h-6 w-6 mr-2 text-blue-500" />
            {t.formTitle}
          </h2>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t.dateLabel} *</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t.scanTypeLabel} *</label>
                <select
                  value={scanType}
                  onChange={(e) => setScanType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                >
                  <option value="">{t.scanTypePlaceholder}</option>
                  <option value="in">{t.scanTypeIn}</option>
                  <option value="out">{t.scanTypeOut}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.reasonLabel} *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t.reasonPlaceholder}
                required
              />
            </div>

            {/* แนบรูปภาพหลักฐาน */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.attachmentLabel}</label>
              {!attachment ? (
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Paperclip className="w-8 h-8 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">{t.attachmentButton}</span></p>
                    </div>
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                  </label>
                </div>
              ) : (
                <div className="relative inline-block w-full text-center">
                  <img src={attachment} alt="Attachment Preview" className="max-h-48 rounded-lg object-contain mx-auto border border-gray-200 shadow-sm" />
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-md transition-colors"
                    title={t.attachmentRemoving}
                  >
                    <AlertCircle className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* ลายเซ็น */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.signatureLabel} *
              </label>
              <div className={`w-full border rounded-md overflow-hidden bg-white ${signatureEmpty && message.text.includes(t.errorSignature) ? 'border-red-500' : 'border-gray-300'}`}>
                <SignatureCanvas
                  ref={sigPad}
                  penColor='black'
                  backgroundColor='white'
                  canvasProps={{ className: 'w-full h-32 cursor-crosshair rounded-md' }}
                  onEnd={() => setSignatureEmpty(sigPad.current.isEmpty())}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  sigPad.current.clear();
                  setSignatureEmpty(true);
                }}
                className="mt-2 text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                {t.signatureClear}
              </button>
            </div>

            {/* แสดงข้อความ Error / Success */}
            {message.text && (
              <div className={`p-4 rounded-md flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" /> : <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />}
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            <div className="border-t pt-6">
              <button
                type="submit"
                disabled={submitting || signatureEmpty}
                className={`w-full py-3 px-6 rounded-md flex items-center justify-center space-x-2 text-lg font-medium transition-colors shadow-sm
                  ${(submitting || signatureEmpty) ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                `}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5" />
                    <span>{t.submitButtonSubmitting}</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>{signatureEmpty ? t.submitButtonSigning : t.submitButtonText}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* --- (3) ประวัติคำร้อง --- */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <List className="h-6 w-6 mr-2 text-blue-500" />
            {t.listTitle}
          </h2>
        </div>
        
        <div className="p-6">
          {loadingList && <div className="flex justify-center items-center h-40"><Loader2 className="h-10 w-10 text-blue-500 animate-spin" /><span className="ml-4 text-gray-600">{t.listLoading}</span></div>}
          {listError && <div className="p-4 rounded-md bg-red-50 text-red-700 flex items-center"><AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" /><span>{listError}</span></div>}

          {!loadingList && !listError && (
            <div className="space-y-4">
              {requests.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500">{t.listEmpty}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderDate}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderType}</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderReason}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderStatus}</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t.listHeaderAction}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {requests.map(req => (
                        <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDateByLang(req.request_date)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${req.scan_type === 'in' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                              {req.scan_type === 'in' ? t.scanTypeIn : t.scanTypeOut}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 max-w-sm truncate" title={req.reason}>{req.reason}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                              {req.status === 'pending' ? t.listStatusPending : req.status === 'approved' ? t.listStatusApproved : t.listStatusRejected}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                            {req.attachment_image && (
                              <button
                                title="ดู/พิมพ์หลักฐาน"
                                onClick={() => handlePrintAttachment(req.attachment_image)}
                                className="text-purple-600 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 p-2 rounded-full transition-colors"
                              >
                                <Paperclip className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ForgotScanRequestForm;